/**
 * Which item is under the pointer.
 *
 * VOSviewer tests labels first and circles second, and it is worth keeping:
 * the label is the part a reader is aiming at, and on a crowded map a heavy
 * item's label often extends well past its own circle over its neighbours'.
 * Testing circles first would make those neighbours unclickable.
 *
 * Both passes run in descending weight order and stop at the first hit, so a
 * big item wins a contested pixel — the same item that is drawn on top.
 *
 * A linear scan over 2000 items per pointer move is about 50 microseconds; a
 * spatial index would be faster and would have to be rebuilt on every zoom,
 * because the label boxes are in screen space.
 */

import type { Scene } from './scene';
import type { Viewport } from '../geometry/viewport';
import { labelAlpha } from './labels';
import { LABEL_MIN_ALPHA } from './constants';

/** Returns the item index under `(px, py)` in CSS pixels, or -1. */
export function hitTest(scene: Scene, vp: Viewport, px: number, py: number): number {
  const order = scene.order;
  const n = order.length;

  for (let rank = 0; rank < n; rank += 1) {
    const i = order[rank] as number;
    // Only a label the user can actually see is clickable. VOSviewer uses its
    // mouse-over minimum alpha here rather than the drawing minimum, so a
    // label that is barely fading in is already a target.
    if (labelAlpha(scene.labelThreshold[i] as number, vp.scale) <= LABEL_MIN_ALPHA) continue;
    const cx = vp.toScreenX(scene.x[i] as number);
    const cy = vp.toScreenY(scene.y[i] as number);
    const halfW = (scene.frameW[i] as number) / 2;
    const halfH = (scene.frameH[i] as number) / 2;
    if (px >= cx - halfW && px <= cx + halfW && py >= cy - halfH && py <= cy + halfH) return i;
  }

  for (let rank = 0; rank < n; rank += 1) {
    const i = order[rank] as number;
    const cx = vp.toScreenX(scene.x[i] as number);
    const cy = vp.toScreenY(scene.y[i] as number);
    const r = (scene.diameter[i] as number) / 2;
    const dx = px - cx;
    const dy = py - cy;
    if (dx * dx + dy * dy <= r * r) return i;
  }

  return -1;
}

/** Nearest item to a screen point, ignoring size. Used by keyboard navigation. */
export function nearestItem(scene: Scene, vp: Viewport, px: number, py: number): number {
  let best = -1;
  let bestDistance = Infinity;
  for (let i = 0; i < scene.n; i += 1) {
    const dx = px - vp.toScreenX(scene.x[i] as number);
    const dy = py - vp.toScreenY(scene.y[i] as number);
    const d = dx * dx + dy * dy;
    if (d < bestDistance) {
      bestDistance = d;
      best = i;
    }
  }
  return best;
}
